<template>
  <div class="mdr-top-panel">
    <div class="start">
      <button
        @click="visibleSettings = 'appLocale'"
        class="mdr-btn-locale-indicator show"
      >
        {{ toUpper(appLocale) }}
      </button>
    </div>
    <div class="mid">
      <span v-if="isTimerVisible" class="mdr-session-timer">
        <Timer :timestamp="callStartedAt" />
      </span>
      <span
        :class="[
          'spacer',
          'd-none',
          { 'mdr-voice-rec-indicator': false, show: false }
        ]"
      ></span>
    </div>
    <div class="end">
      <button
        v-if="
          isTranslatorEnabled && endpointStatusCode === EndpointStatus.connected
        "
        @click="visibleSettings = 'sourceLanguage'"
        class="mdr-btn-translate-indicator show"
      >
        {{ ISO6391.getNativeName(first(split(sourceLanguage, "-"))) }}
      </button>
      <FullscreenButton v-if="isFullscreenButtonVisible" />
    </div>
  </div>
  <div
    v-if="
      visibleSettings === 'appLocale' ||
        (isKioskType && keys(appLocales).length > 1)
    "
    class="mdr-settings"
  >
    <div class="mdr-scrollable-v">
      <div class="menu-group">
        <button
          v-for="(l, i) in appLocales"
          :key="i"
          @click="handleAppLocaleSelect(i)"
          :class="{ active: i === appLocale }"
          :disabled="i === appLocale"
        >
          {{ l }}
        </button>
      </div>
    </div>
    <div class="header">
      <button @click="visibleSettings = null" class="mdr-btn-close" />
    </div>
  </div>

  <div v-else-if="visibleSettings === 'sourceLanguage'" class="mdr-settings">
    <div class="mdr-scrollable-v">
      <div class="menu-group">
        <button
          v-for="l of $enum(HumanSpeechLanguage)?.getValues()"
          :key="l"
          @click="handleSourceLanguageChange(l)"
          :class="{ active: sourceLanguage === l }"
          :disabled="sourceLanguage === l"
        >
          {{ getLangName(l, true) }}
        </button>
      </div>
    </div>
    <div class="header">
      <button @click="visibleSettings = null" class="mdr-btn-close" />
    </div>
  </div>
</template>
<script lang="ts">
import { useStore } from "vuex";
import { computed, ref, watch } from "vue";
import _ from "lodash";
import FullscreenButton from "@/components/FullscreenButton.vue";
import ISO6391 from "iso-639-1";
import {
  HumanSpeechLanguage,
  CallQueuePositionChangedReason,
  DeviceType,
  EndpointStatus,
  MessageType,
  RequestedAction
} from "@moderrotech/servers-communication-library/src/types";
import moment from "moment/moment";
import Timer from "@/components/Timer.vue";
import { getLangName } from "@moderrotech/servers-communication-library/src/helpers/lang";
import { getDeviceType } from "@moderrotech/servers-communication-library/src/helpers/device";
import { $enum } from "ts-enum-util";

export default {
  components: {
    Timer,
    FullscreenButton
  },
  setup() {
    const store = useStore(),
      getProperty = (name: string) => store.getters["auth/property"](name);
    const endpointStatusCode = computed(() => store.getters.endpointStatusCode),
      isVideoOn = computed(() => store.getters.isVideoOn),
      isCameraEnabled = computed(
        () => store.state.session.mediaDevices.isCameraEnabled
      );

    const handleCameraEnableClick = async () =>
        await store.dispatch("session/mediaDevices/enableCamera"),
      handleCameraDisableClick = async () =>
        await store.dispatch("session/mediaDevices/disableCamera");

    const callStartedAt = ref<number | null>(null);

    const visibleSettings = ref<"appLocale" | "sourceLanguage" | null>(null);

    watch(
      () => endpointStatusCode.value === EndpointStatus.connected,
      val => {
        if (val) {
          callStartedAt.value = moment().unix();
        } else {
          callStartedAt.value = null;
          if (visibleSettings.value === "sourceLanguage") {
            visibleSettings.value = null;
          }
        }
      },
      { immediate: true }
    );

    const isTranslatorEnabled = computed(() =>
      getProperty("aws.translator.enabled")
    );

    const sourceLanguage = computed(
      (): HumanSpeechLanguage | null =>
        store.state.session.sourceLanguage ??
        getProperty("aws.translation.source.language") ??
        null
    );

    const supportedLocales = computed((): string[] =>
        _.keys(
          _.pickBy(
            store.state.config.config.appLocalesOrder ?? {},
            v => +v >= 0
          )
        )
      ),
      appLocale = computed(
        (): "en" | "ca" | "es" | "he" | "pt" | "ja" | null =>
          store.state.config.config.appLocale
      );

    watch(
      () => sourceLanguage.value,
      (newVal, oldVal) => {
        // source language has been changed manually (by a client or an expert)
        if (
          oldVal &&
          newVal &&
          supportedLocales.value.includes(_.first(_.split(newVal, "-"))!)
        ) {
          store.commit("config/PATCH_CONFIG", {
            appLocale: _.first(_.split(newVal, "-"))
          });
        }
      },
      { immediate: true }
    );

    watch(
      () => [
        endpointStatusCode.value === EndpointStatus.connected,
        sourceLanguage.value
      ],
      ([v1, v2]) => {
        if (v1 && v2) {
          store.dispatch("session/sendJsonMessage", {
            message: {
              type: MessageType.awsTranslationTargetLanguage,
              languageCode: v2
            }
          });
        }
      },
      { immediate: true }
    );

    const requestedRemoteActions = computed(
      (): { action: RequestedAction; payload?: any }[] | null =>
        store.state.session.requestedActions
    );

    watch(
      () =>
        !!_.find(
          requestedRemoteActions.value,
          v =>
            v.action === RequestedAction.showTranslationSourceLanguageSelector
        ),
      v => {
        if (v) {
          store.commit(
            "session/UNSET_REQUESTED_ACTION",
            RequestedAction.showTranslationSourceLanguageSelector
          );
          visibleSettings.value = "sourceLanguage";
        }
      },
      { immediate: true }
    );

    watch(
      () =>
        _.find(
          requestedRemoteActions.value,
          v => v.action === RequestedAction.setTranslationSourceLanguage
        ),
      v => {
        if (v) {
          store.commit("session/SET_SOURCE_LANGUAGE", v.payload);
          store.commit(
            "session/UNSET_REQUESTED_ACTION",
            RequestedAction.setTranslationSourceLanguage
          );
        }
      },
      { immediate: true }
    );

    return {
      isKioskType: computed(() => store.getters["auth/isKioskType"]),
      visibleSettings,
      handleAppLocaleSelect: (locale: string) => {
        store.commit("config/PATCH_CONFIG", { appLocale: locale });
        visibleSettings.value = null;
      },
      appLocales: computed(() => {
        const keys = _.map(
            [..._.keys(Array(supportedLocales.value.length))],
            v =>
              _.findKey(
                store.state.config.config.appLocalesOrder,
                l => l === +v
              )!
          ),
          values = _.map(keys, k => ISO6391.getNativeName(k));

        return _.zipObject(keys, values);
      }),
      appLocale,
      isVideoOn,
      isCameraEnabled,
      handleCameraEnableClick,
      handleCameraDisableClick,
      callStartedAt,
      isTimerVisible: computed(
        () =>
          [EndpointStatus.placingCall, EndpointStatus.connected].includes(
            endpointStatusCode.value
          ) ||
          (store.state.queue.position !== null &&
            store.state.queue.position >= 0 &&
            ![
              CallQueuePositionChangedReason.noExperts,
              CallQueuePositionChangedReason.noTopic,
              CallQueuePositionChangedReason.timeout
            ].includes(store.state.queue.positionChangeReason))
      ),
      isFullscreenButtonVisible: !_.includes(
        [DeviceType.mobile, DeviceType.tablet],
        getDeviceType()
      ),
      isTranslatorEnabled,
      sourceLanguage,
      handleSourceLanguageChange: (l: HumanSpeechLanguage) => {
        store.commit("session/SET_SOURCE_LANGUAGE", l);
        visibleSettings.value = null;
      },
      endpointStatusCode,
      EndpointStatus,
      getLangName,
      HumanSpeechLanguage,
      ISO6391,
      $enum,
      ..._.pick(_, ["toUpper", "first", "split", "keys"])
    };
  }
};
</script>
<style lang="scss">
@import "@/assets/styles/app-settings.scss";
</style>
